import { createWorkspaceRoomId } from "../domain/share";
import { renderBrandMark } from "./brand";

type LandingAccess = "edit" | "view";

const STARTER_ROOM = "syncrel-start";

const frameworkNames = [
  "RICE",
  "Story Map",
  "Revenue",
  "Kano",
  "OKRs",
  "JTBD",
  "North Star",
  "MoSCoW",
  "OST",
  "WSJF",
  "ICE",
  "Now Next Later",
];

const compareRows = [
  {
    title: "Syncrel vs Miro and FigJam",
    body: "Whiteboards are great for exploration. Syncrel keeps that canvas feel, then links every note to framework fields, specs, polls, and exportable data.",
  },
  {
    title: "Syncrel vs Eraser",
    body: "Eraser is excellent for technical diagrams and docs. Syncrel focuses earlier in the product loop: deciding what matters, structuring it, then writing the spec beside the map.",
  },
  {
    title: "Syncrel vs Fibery and Notion",
    body: "Modular workspaces can model almost anything, but setup becomes its own project. Syncrel starts with product frameworks and keeps the workflow opinionated.",
  },
  {
    title: "Syncrel vs spreadsheets and forms",
    body: "Tables and forms collect fields. Syncrel keeps the thinking visible, so structured data stays connected to the messy canvas where decisions actually happen.",
  },
];

const brandMark = renderBrandMark("landing-brand__mark");

export const landingWorkspaceUrl = (room = STARTER_ROOM, access: LandingAccess = "edit"): string => {
  const encodedRoom = encodeURIComponent(room);
  return access === "view"
    ? `?room=${encodedRoom}&access=view`
    : `?room=${encodedRoom}`;
};

export const renderLandingPage = (startRoom = createWorkspaceRoomId()): string => `
  <main class="landing-shell">
    <header class="landing-header">
      <a class="landing-brand" href="/" aria-label="Syncrel home">
${brandMark}
        <strong>Syncrel</strong>
      </a>
      <nav class="landing-nav" aria-label="Landing navigation">
        <a href="#workflow">Workflow</a>
        <a href="#system">Product</a>
        <a href="#frameworks">Frameworks</a>
        <a href="#compare">Compare</a>
        <a href="#start">Start</a>
      </nav>
      <a class="landing-header__cta" href="${landingWorkspaceUrl(startRoom)}" data-landing-app-link>Open workspace</a>
    </header>

    <section class="landing-hero" aria-labelledby="landing-title">
      <div class="landing-hero__copy">
        <h1 id="landing-title">Syncrel</h1>
        <p class="landing-hero__lede">Canvas ideation linked to frameworks, fields, specs, and polls.</p>
        <p class="landing-hero__support">
          Turn fuzzy product strategy into a browser-based workspace where maps, prioritization models,
          technical specs, and reader feedback stay connected.
        </p>
        <div class="landing-actions" aria-label="Primary actions">
          <a class="landing-button landing-button--primary" href="${landingWorkspaceUrl(startRoom)}" data-landing-app-link>Start workspace</a>
          <a class="landing-button landing-button--secondary" href="${landingWorkspaceUrl(STARTER_ROOM, "view")}">Open view-only</a>
        </div>
        <dl class="landing-signal-strip" aria-label="Product signals">
          <div>
            <dt>Canvas</dt>
            <dd>Ideate freely</dd>
          </div>
          <div>
            <dt>Fields</dt>
            <dd>Structure fast</dd>
          </div>
          <div>
            <dt>Specs</dt>
            <dd>Write in context</dd>
          </div>
        </dl>
      </div>

      <section class="landing-product-preview" aria-label="Syncrel product preview">
        <div class="landing-preview-tabs" aria-label="Framework views">
          <span>RICE</span>
          <span>Story Map</span>
          <span>Revenue</span>
          <span>+</span>
        </div>
        <div class="landing-preview-canvas">
          <div class="landing-preview-rail" aria-hidden="true">
            <span>S</span>
            <span></span>
            <span></span>
          </div>
          <article class="landing-preview-card landing-preview-card--blue">
            <small>Feature</small>
            <strong>Offline client voting pack</strong>
            <span>RICE 367.5</span>
          </article>
          <article class="landing-preview-card landing-preview-card--green">
            <small>Spec</small>
            <strong>Usage-based billing</strong>
            <span>Priority High</span>
          </article>
          <article class="landing-preview-card landing-preview-card--red">
            <small>Entity</small>
            <strong>Billing plan</strong>
            <span>Starter notes</span>
          </article>
          <article class="landing-preview-card landing-preview-card--amber">
            <small>Poll</small>
            <strong>Adopt usage-based pricing?</strong>
            <span>57 votes</span>
          </article>
        </div>
        <aside class="landing-preview-spec" aria-label="Spec document preview">
          <small>Spec document</small>
          <h2>Usage-based billing</h2>
          <p>Write the PRD beside the canvas, add inline polls, and keep decisions connected to the entity map.</p>
          <ul>
            <li>Inline polls</li>
            <li>Framework values</li>
            <li>Engineering handoff</li>
          </ul>
        </aside>
        <div class="landing-preview-fields" aria-label="Fields table preview">
          <strong>Fields table</strong>
          <span>Name</span>
          <span>Status</span>
          <span>Framework Library</span>
          <span>Usage billing</span>
          <span>In progress</span>
          <span>Revenue</span>
        </div>
      </section>
    </section>

    <section class="landing-section landing-workflow" id="workflow">
      <div class="landing-section__head">
        <span>Workflow</span>
        <h2>Select framework &gt; customise nodes &gt; add spec docs + polls &gt; share links.</h2>
        <p>Syncrel makes the product strategy loop explicit: start with a framework, shape the map, write the spec beside it, then invite teammates to read and vote.</p>
      </div>
      <div class="landing-workflow__steps" aria-label="Syncrel workflow">
        <article>
          <span>01</span>
          <h3>Select framework</h3>
          <p>Pick RICE, Story Map, Revenue, Kano, OKRs, JTBD, or another model to turn the canvas into a focused workspace.</p>
        </article>
        <article>
          <span>02</span>
          <h3>Customise nodes</h3>
          <p>Edit cards inline, add fields, connect entities, and keep each framework view clean instead of mixing every idea together.</p>
        </article>
        <article>
          <span>03</span>
          <h3>Add spec docs + polls</h3>
          <p>Open the split spec, write the decision narrative, and place polls directly inside the doc where context is fresh.</p>
        </article>
        <article>
          <span>04</span>
          <h3>Share edit and view links</h3>
          <p>Send edit access to collaborators or view-only access to readers so teammates can read, vote, and respond in context.</p>
        </article>
      </div>
      <div class="landing-workflow__share" aria-label="Share loop">
        <strong>Share loop</strong>
        <span>Edit link for builders</span>
        <span>View link for readers</span>
        <span>Unique poll votes after the spec is read</span>
      </div>
    </section>

    <section class="landing-section" id="system">
      <div class="landing-section__head">
        <span>SYS 01</span>
        <h2>The missing middle between whiteboards, docs, and databases.</h2>
        <p>Syncrel is built for technical product thinking: ideate visually, choose a framework, shape the fields, write the spec, and collect feedback without losing context.</p>
      </div>
      <div class="landing-system-grid">
        <article>
          <span>01</span>
          <h3>Map the idea</h3>
          <p>Use the canvas for entities, notes, relations, and quick expansion from any card.</p>
        </article>
        <article>
          <span>02</span>
          <h3>Apply structure</h3>
          <p>Switch frameworks like RICE, Story Map, Revenue, Kano, OKRs, JTBD, and more.</p>
        </article>
        <article>
          <span>03</span>
          <h3>Write the spec</h3>
          <p>Keep the doc in split view so the canvas remains visible while decisions become prose.</p>
        </article>
        <article>
          <span>04</span>
          <h3>Validate with polls</h3>
          <p>Add polls directly inside the spec so unique readers can vote after reading.</p>
        </article>
      </div>
    </section>

    <section class="landing-section landing-framework-band" id="frameworks">
      <div class="landing-section__head">
        <span>SYS 02</span>
        <h2>Start with a proven framework, then make it yours.</h2>
        <p>Every added framework creates starter notes and editable fields, so the workspace gives you momentum instead of a blank grid.</p>
      </div>
      <div class="landing-framework-list" aria-label="Framework Library">
        ${frameworkNames.map((name) => `<span>${name}</span>`).join("")}
      </div>
    </section>

    <section class="landing-section" id="compare">
      <div class="landing-section__head">
        <span>SYS 03</span>
        <h2>Clear comparisons for product teams choosing a workspace.</h2>
        <p>Syncrel is not another generic whiteboard. It is a focused product strategy workspace for turning visual thinking into structured execution.</p>
      </div>
      <div class="landing-comparison">
        ${compareRows.map((row) => `
          <article>
            <h3>${row.title}</h3>
            <p>${row.body}</p>
          </article>
        `).join("")}
      </div>
    </section>

    <section class="landing-section landing-negative">
      <div>
        <span>SYS 04</span>
        <h2>What Syncrel deliberately is not</h2>
      </div>
      <ul>
        <li><strong>It is not a survey product.</strong> Polling lives inside specs because feedback should be tied to the decision being explained.</li>
        <li><strong>It is not a generic project tracker.</strong> The core flow is ideation, frameworks, fields, specs, and handoff.</li>
        <li><strong>It is not a diagram export toy.</strong> The canvas is the working surface for product strategy, not the final artifact by itself.</li>
      </ul>
    </section>

    <section class="landing-start" id="start">
      <div>
        <span>GET STARTED</span>
        <h2>Your product thinking, connected and clear.</h2>
        <p>Open a room, choose a framework, and begin with starter notes that already know how to become fields and specs.</p>
      </div>
      <div class="landing-start__actions">
        <a class="landing-button landing-button--primary" href="${landingWorkspaceUrl(startRoom)}" data-landing-app-link>Open Syncrel</a>
        <a class="landing-button landing-button--secondary" href="#compare">Compare first</a>
      </div>
    </section>

    <footer class="landing-footer">
      <span>Syncrel</span>
      <p>
        built by <a href="https://tanziro.com/" target="_blank" rel="noreferrer">tanziro.com</a>
        &middot; <a href="https://buymeacoffee.com/tanzir" target="_blank" rel="noreferrer">buy me a coffee</a>
      </p>
    </footer>
  </main>
`;
